// src/components/PaymeoMessagingPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
    fetchAsksData, 
    submitOfferToAsk,
    fetchOffersForAsk 
} from '../services/asksService';
import { isAuthenticated, api } from '../utils/api';
import { API_CONFIG } from '../utils/apiConfig';
import { fetchAskMessages, sendAskMessage } from '../services/chatProxyService';
import { useAuth } from '../contexts/AuthContext';
import LocationPicker from '../components/LocationPicker';
import {
    X,
    Image,
    MapPin,
    Banknote,
    ChevronLeft,
    ChevronRight,
    Check,
    Trash2,
    Upload,
    Package,
    Truck,
    FileText
} from 'lucide-react';

// Convert a (already normalized by asksService.formatAsk) ask to
// conversation format. Falls back to raw fields when given raw data.
const stripHtml = (value) =>
    String(value || '')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/p>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/\n{3,}/g, '\n\n')
        .replace(/[ \t]+\n/g, '\n')
        .replace(/\\u([0-9a-fA-F]{4})/g, (m, h) => String.fromCodePoint(parseInt(h, 16)))
        .replace(/(^|[^\w])u([0-9a-fA-F]{4})/g, (m, pre, h) => pre + String.fromCodePoint(parseInt(h, 16)))
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .trim();

const convertAskToConversation = (ask) => {
    const now = Date.now();
    
    // The service decodes parsed_json.formValues, so prefer the formatted
    // fields and only fall back to the raw shape.
    const isExpired =
        typeof ask.isExpired === 'boolean'
            ? ask.isExpired
            : ask.formatted_expires === 'Expired' || new Date(ask.expires_at) < new Date();
    const status =
        ask.status ||
        (isExpired ? 'expired' : ask.hasResponded ?? ask.has_responded ? 'responded' : 'active');
    const description =
        ask.description || ask.search_query || ask.parsed_json?.interpreted_intent ||
        `I need ${ask.parsed_json?.attributes?.join(', ') || 'this item'}`;
    const body = ask.messageBody || stripHtml(ask.message_body) || description;
    const buyerName = ask.buyerName || ask.buyer_name || 'Unknown Buyer';
    
    return {
        id: ask.id,
        askId: ask.id,
        title: ask.title || `Ask #${String(ask.id).slice(0, 8)}`,
        snippet: ask.snippet || body.substring(0, 100),
        unread: 0,
        avatarColor: 'bg-blue-400',
        status,
        price: ask.price || null,
        location: ask.location || 'Location not specified',
        category: ask.category || 'other',
        createdAt: ask.createdAt || ask.created_at,
        buyerName,
        avatarUrl: ask.buyerLogo || ask.buyer_logo || null,
        qualityScore: ask.qualityScore ?? ask.quality_score,
        responseCount: ask.responseCount ?? ask.response_count,
        isExpired,
        expiresAt: ask.expiresAt || ask.expires_at,
        formattedExpires: ask.formattedExpires || ask.formatted_expires,
        flowId: null, // set once the buyer accepts an offer and a real flow exists
        messages: [
            {
                id: `m-${now}-1`,
                sender: 'buyer',
                senderName: buyerName,
                text: body,
                time: ask.createdAt || ask.created_at,
                images: ask.imageUrls || [],
                reactions: {},
                askDetails: ask
            }
        ]
    };
};


// Map a backend `flow_messages` row (from the mobile backend) into the
// ChatFlow conversation-message shape. Messages sent by the current seller
// (matching company_id) render on the right.
const mapBackendMessage = (m, myCompanyId) => {
    let images = [];
    if (Array.isArray(m.image_urls)) images = m.image_urls;
    else if (typeof m.image_urls === 'string' && m.image_urls) {
        try { images = JSON.parse(m.image_urls); } catch (e) { /* ignore */ }
    }
    const isOffer = m.type === 'offer';
    const mine = String(m.sender_id) === String(myCompanyId);
    let offerData = null;
    if (isOffer) {
        let md = {};
        if (typeof m.message_data === 'string' && m.message_data) {
            try { md = JSON.parse(m.message_data); } catch (e) { /* ignore */ }
        } else if (m.message_data && typeof m.message_data === 'object') {
            md = m.message_data;
        }
        offerData = {
            price: md.price || 0,
            deliveryOption: md.deliveryOption || 'pickup',
            personalNote: m.content || '',
            quantity: md.quantity || 1,
            location: md.location || '',
            timestamp: m.created_at
        };
    }
    return {
        id: `flow-${m.id}`,
        sender: mine ? 'seller' : 'buyer',
        senderName: mine ? 'You' : (m.sender_name || 'Buyer'),
        text: m.content || (m.type === 'offer' ? (mine ? '💰 Offer Sent' : 'Offer') : ''),
        time: m.created_at || new Date().toISOString(),
        images,
        file: null,
        reactions: {},
        isOffer,
        offerData,
        _serverId: m.id
    };
};


// Offer Creator Component
const OfferCreator = ({ selectedAsk, offerData, onOfferDataChange, onSend, onCancel }) => {
    const [localOfferData, setLocalOfferData] = useState({
        price: offerData.price || '',
        location: offerData.location || '',
        latitude: offerData.latitude || null,
        longitude: offerData.longitude || null,
        deliveryOption: offerData.deliveryOption || 'pickup',
        personalNote: offerData.personalNote || '',
        media: offerData.media || null,
        quantity: offerData.quantity || 1
    });
    const [mediaPreview, setMediaPreview] = useState(offerData.media);
    const mediaRef = useRef(null);

    const handleLocalInputChange = (field, value) => {
        const updated = { ...localOfferData, [field]: value };
        setLocalOfferData(updated);
        onOfferDataChange(field, value);
    };

    const handleMediaChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const isVideo = file.type.startsWith('video/');
        const mediaData = {
            file,
            type: isVideo ? 'video' : 'image',
            name: file.name
        };

        if (isVideo) {
            const videoUrl = URL.createObjectURL(file);
            setMediaPreview({ ...mediaData, url: videoUrl });
        } else {
            const reader = new FileReader();
            reader.onload = function (ev) {
                setMediaPreview({ ...mediaData, url: ev.target.result });
            };
            reader.readAsDataURL(file);
        }
        setLocalOfferData(prev => ({ ...prev, media: mediaData }));
        onOfferDataChange('media', mediaData);
    };

    const removeMedia = () => {
        if (mediaPreview?.type === 'video' && mediaPreview?.url) {
            try { URL.revokeObjectURL(mediaPreview.url); } catch (err) {}
        }
        setMediaPreview(null);
        setLocalOfferData(prev => ({ ...prev, media: null }));
        onOfferDataChange('media', null);
        if (mediaRef.current) mediaRef.current.value = null;
    };

    const handleSend = () => {
        if (!localOfferData.price || !localOfferData.location) {
            alert('Please fill in price and location');
            return;
        }
        onSend(localOfferData);
    };

    return (
        <div className="fixed inset-0 z-[400] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-0 sm:p-4 animate-backdrop-in">
            <button
                aria-label="Close modal"
                onClick={onCancel}
                className="absolute inset-0 cursor-default"
            />
            <div className="relative w-full sm:w-[480px] bg-white dark:bg-[#16181c] rounded-t-[24px] sm:rounded-[28px] overflow-hidden shadow-2xl shadow-black/30 sm:max-h-[92vh] flex flex-col animate-modal-in">
                {/* Mobile grab handle */}
                <div className="pt-2.5 pb-0.5 sm:hidden">
                    <div className="mx-auto w-10 h-1 rounded-full bg-gray-200 dark:bg-white/15" />
                </div>

                {/* Header */}
                <div className="px-5 py-3.5 flex items-center justify-between shrink-0 border-b border-gray-100 dark:border-white/10">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-blue-50 dark:bg-blue-500/10 flex items-center justify-center">
                            <Banknote className="w-4.5 h-4.5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h2 className="text-[15px] font-semibold text-gray-900 dark:text-white leading-tight">Send Offer</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-[260px]">{selectedAsk?.title}</p>
                        </div>
                    </div>
                    <button
                        onClick={onCancel}
                        aria-label="Close"
                        className="p-2 -mr-1 hover:bg-gray-100 dark:hover:bg-white/10 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Body */}
                <div className="overflow-y-auto flex-1 custom-scrollbar px-5 py-5 space-y-4">
                    {/* Media Upload */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">Product Media <span className="font-normal text-gray-400">(optional)</span></label>
                        <div className="flex gap-3 items-center">
                            <input
                                ref={mediaRef}
                                type="file"
                                accept="image/*,video/*"
                                onChange={handleMediaChange}
                                className="hidden"
                            />
                            <button
                                onClick={() => mediaRef.current?.click()}
                                className="flex-1 px-4 py-3 border-2 border-dashed border-gray-300 dark:border-white/15 rounded-2xl hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/5 transition text-gray-600 dark:text-gray-300 flex items-center justify-center gap-2 text-sm"
                            >
                                <Image className="w-5 h-5 text-blue-500" />
                                Add Photo / Video
                            </button>
                            {mediaPreview && (
                                <button
                                    onClick={removeMedia}
                                    aria-label="Remove media"
                                    className="px-3 py-3 bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400 rounded-2xl hover:bg-red-100 dark:hover:bg-red-500/20 transition"
                                >
                                    <Trash2 className="w-5 h-5" />
                                </button>
                            )}
                        </div>
                        {mediaPreview && (
                            <div className="relative mt-3 h-40 rounded-2xl overflow-hidden bg-black">
                                {mediaPreview.type === 'video' ? (
                                    <video src={mediaPreview.url} controls muted playsInline className="w-full h-full object-cover" />
                                ) : (
                                    <img src={mediaPreview.url} alt="Offer media" className="w-full h-full object-cover" />
                                )}
                            </div>
                        )}
                        <p className="text-xs text-gray-500 mt-1.5">Add a photo or video to make your offer stand out</p>
                    </div>

                    {/* Offer Price */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">Offer Price (₦) <span className="text-red-500">*</span></label>
                        <div className="relative">
                            <Banknote className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="number"
                                value={localOfferData.price}
                                onChange={(e) => handleLocalInputChange('price', e.target.value)}
                                placeholder="Enter your price"
                                className="w-full pl-10 py-3 pr-4 border border-gray-300 dark:border-white/15 dark:bg-transparent rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400"
                            />
                        </div>
                    </div>

                    {/* Quantity */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">Quantity</label>
                        <div className="relative">
                            <Package className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <input
                                type="number"
                                value={localOfferData.quantity}
                                onChange={(e) => handleLocalInputChange('quantity', e.target.value)}
                                placeholder="Quantity (default: 1)"
                                min="1"
                                className="w-full pl-10 py-3 pr-4 border border-gray-300 dark:border-white/15 dark:bg-transparent rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent placeholder:text-gray-400"
                            />
                        </div>
                    </div>

                    {/* Location */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">Your Location <span className="text-red-500">*</span></label>
                        <LocationPicker
                            initialQuery={localOfferData.location}
                            onSelect={(loc) => {
                                handleLocalInputChange('location', loc.name);
                                handleLocalInputChange('latitude', loc.lat != null ? parseFloat(loc.lat) : null);
                                handleLocalInputChange('longitude', loc.lon != null ? parseFloat(loc.lon) : null);
                            }}
                        />
                        {localOfferData.location && (
                            <p className="text-xs text-green-600 dark:text-green-400 mt-2 flex items-center gap-1.5 truncate">
                                <Check className="w-3.5 h-3.5" /> {localOfferData.location}
                            </p>
                        )}
                    </div>

                    {/* Delivery Option */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">Delivery Option</label>
                        <div className="relative">
                            <Truck className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                            <select
                                value={localOfferData.deliveryOption}
                                onChange={(e) => handleLocalInputChange('deliveryOption', e.target.value)}
                                className="w-full pl-10 py-3 pr-4 border border-gray-300 dark:border-white/15 dark:bg-transparent rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent appearance-none"
                            >
                                <option value="pickup">Pickup Only</option>
                                <option value="home delivery">Home Delivery</option>
                                <option value="negotiable">Negotiable</option>
                            </select>
                        </div>
                    </div>

                    {/* Personal Note */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-800 dark:text-gray-100 mb-2">Personal Note <span className="font-normal text-gray-400">(optional)</span></label>
                        <div className="relative">
                            <FileText className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                            <textarea
                                value={localOfferData.personalNote}
                                onChange={(e) => handleLocalInputChange('personalNote', e.target.value)}
                                placeholder="Add a personal message to the buyer..."
                                rows="3"
                                className="w-full pl-10 py-3 pr-4 border border-gray-300 dark:border-white/15 dark:bg-transparent rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none placeholder:text-gray-400"
                            />
                        </div>
                        <p className="text-xs text-gray-500 mt-1.5">This will appear on the offer card</p>
                    </div>
                </div>

                {/* Actions */}
                <div className="px-5 py-4 border-t border-gray-100 dark:border-white/10 flex gap-3 shrink-0">
                    <button
                        onClick={onCancel}
                        className="flex-1 px-6 py-3 border border-gray-200 dark:border-white/15 text-gray-700 dark:text-gray-300 rounded-2xl hover:bg-gray-50 dark:hover:bg-white/5 transition font-semibold text-sm"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSend}
                        disabled={!localOfferData.price || !localOfferData.location}
                        className={`flex-1 px-6 py-3 rounded-2xl font-semibold text-sm transition flex items-center justify-center gap-2 ${
                            !localOfferData.price || !localOfferData.location
                                ? 'bg-gray-100 dark:bg-white/10 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
                        }`}
                    >
                        <Banknote className="w-4 h-4" />
                        Send Offer
                    </button>
                </div>
            </div>
        </div>
    );
};

export default function PaymeoMessagingPage() {
    const { currentUser } = useAuth();
    const [conversations, setConversations] = useState([]);
    const [activeConvId, setActiveConvId] = useState(null);
    const [messageText, setMessageText] = useState('');
    const [sending, setSending] = useState(false);
    const [filePreview, setFilePreview] = useState(null);
    const [notes, setNotes] = useState({});
    const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
    const [isMobileDetailsOpen, setIsMobileDetailsOpen] = useState(false);
    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    const [showFileOptions, setShowFileOptions] = useState(false);
    const [showOfferCreator, setShowOfferCreator] = useState(false);
    const [selectedAsk, setSelectedAsk] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isAuth, setIsAuth] = useState(isAuthenticated());
    const [offerData, setOfferData] = useState({
        price: '',
        location: '',
        latitude: null,
        longitude: null,
        deliveryOption: 'pickup',
        personalNote: '',
        media: null,
        quantity: 1
    });
    
    const fileRef = useRef(null);
    const imageRef = useRef(null);
    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    // Check authentication status
    useEffect(() => {
        setIsAuth(isAuthenticated());
        
        // Listen for auth changes
        const handleAuthUpdate = () => {
            setIsAuth(isAuthenticated());
        };
        
        window.addEventListener('auth:token-updated', handleAuthUpdate);
        window.addEventListener('auth:unauthorized', () => {
            setIsAuth(false);
            setError('Session expired. Please log in again.');
            showAlertMessage('Session expired. Please log in again.', true);
        });
        
        return () => {
            window.removeEventListener('auth:token-updated', handleAuthUpdate);
            window.removeEventListener('auth:unauthorized', handleAuthUpdate);
        };
    }, []);

    // Fetch asks on component mount
    useEffect(() => {
        if (isAuth) {
            loadAsks();
        } else {
            setLoading(false);
            setError('Authentication required. Please log in to view asks.');
        }
    }, [isAuth]);

    useEffect(() => {
        scrollToBottom();
    }, [activeConvId, conversations]);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
        }
    }, [messageText]);

    const scrollToBottom = () => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const showAlertMessage = (message, isError = false) => {
        setAlertMessage(typeof message === 'string' ? message : JSON.stringify(message));
        setShowAlert(true);
        setTimeout(() => setShowAlert(false), 3000);
    };

const loadAsks = async () => {
    setLoading(true);
    setError(null);
    try {
        const result = await fetchAsksData({ page: 1, limit: 50 });

        // fetchAsksData returns the normalized asks array in result.data
        const finalResponse = result.data;

        if (result.success && finalResponse && Array.isArray(finalResponse)) {
            const asksData = finalResponse;
            
            console.log(`Found ${asksData.length} asks to display`);
            
            if (asksData.length === 0) {
                console.log('No asks available');
                setConversations([]);
                setLoading(false);
                return;
            }
            
            // Convert asks to conversations
            const conversationsList = asksData.map(ask => convertAskToConversation(ask));
            
            console.log(`Converted ${conversationsList.length} conversations`);
            
            setConversations(conversationsList);
            
            // Set active conversation if not already set
            if (conversationsList.length > 0 && !activeConvId) {
                setActiveConvId(conversationsList[0].id);
            }
        } else {
            console.error('Failed to load asks:', result.error);
            setError(result.error || 'Failed to load asks');
            showAlertMessage(result.error || 'Failed to load asks', true);
            setConversations([]);
        }
    } catch (err) {
        console.error('Exception loading asks:', err);
        setError(err.message);
        showAlertMessage('Failed to load asks: ' + err.message, true);
        setConversations([]);
    } finally {
        setLoading(false);
    }
};
    const activeConv = conversations.find((c) => c.id === activeConvId) || conversations[0];

    // Check if an offer has been sent in this conversation
    const hasSentOffer = activeConv?.messages?.some(m => m.isOffer);

    const handleSelectConversation = (id) => {
        setActiveConvId(id);
        setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, unread: 0 } : c)));
        if (window.innerWidth < 768) {
            setIsMobileSidebarOpen(false);
            setIsMobileDetailsOpen(false);
        }
        loadRealMessages(id);
    };

    // Fetch the persisted chat messages for a conversation (only exist once
    // the buyer has accepted an offer, creating a real flow). Merges them
    // into the local conversation state and records the flowId.
    const loadRealMessages = async (convId) => {
        const conv = conversations.find((c) => c.id === convId);
        const askId = conv?.askId || conv?.id;
        if (!askId) return;

        const result = await fetchAskMessages(askId);
        if (!result.success) return;

        const payload = result.data?.data || result.data || {};
        const rows = Array.isArray(payload) ? payload : payload.messages || [];
        const flowId = payload.flow_id || result.data?.flow_id || null;

        setConversations((prev) =>
            prev.map((c) => {
                if (String(c.id) !== String(convId)) return c;
                const serverMsgs = rows
                    .filter((m) => m && m.id)
                    .map((m) => mapBackendMessage(m, currentUser?.company_id));
                const localMsgs = (c.messages || []).filter((m) => !m._serverId);
                const byId = new Map();
                serverMsgs.forEach((m) => byId.set(m.id, m));
                c.messages?.forEach((m) => { if (m._serverId) byId.set(m.id, m); });
                return { ...c, flowId, messages: [...Array.from(byId.values()), ...localMsgs] };
            })
        );
    };

    // Poll periodically for new persisted messages (webapp replies) while a
    // conversation is active, so replies arrive without a manual refresh.
    const firstRenderRef = useRef(true);
    useEffect(() => {
        if (firstRenderRef.current) {
            firstRenderRef.current = false;
            return;
        }
        if (!activeConvId || !isAuth) return;
        const timer = setInterval(() => {
            loadRealMessages(activeConvId);
        }, 5000);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeConvId, isAuth]);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        if (file.type.startsWith('image/')) {
            const reader = new FileReader();
            reader.onload = function (ev) {
                setFilePreview({ name: file.name, dataUrl: ev.target.result, type: 'image' });
            };
            reader.readAsDataURL(file);
        } else {
            setFilePreview({ name: file.name, type: 'file', size: (file.size / 1024 / 1024).toFixed(2) + ' MB' });
        }
        setShowFileOptions(false);
    };

    const sendMessage = async () => {
        if (!messageText.trim() && !filePreview) return;
        setSending(true);

        const newMsg = {
            id: `m-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            sender: 'seller',
            senderName: 'You',
            text: messageText.trim() || '',
            time: new Date().toISOString(),
            images: filePreview?.type === 'image' ? [filePreview.dataUrl] : [],
            file: filePreview?.type === 'file' ? filePreview : null,
            reactions: {}
        };

        const askId = activeConv?.askId || activeConv?.id;

        // Try to persist + deliver in real time via the proxy. This only
        // succeeds once the buyer has accepted an offer (a flow exists); the
        // mobile backend then emits 'new_message' to the webapp room.
        if (askId) {
            const uploadedImages = newMsg.images.filter(u => typeof u === 'string' && u.startsWith('http'));
            const sendResult = await sendAskMessage(askId, {
                content: newMsg.text,
                image_urls: uploadedImages.length ? uploadedImages : null,
                video_urls: null
            });
            if (sendResult.success) {
                const serverData = sendResult.data?.data ? { ...sendResult.data.data, sender_name: 'You' } : sendResult.data;
                const serverMsg = serverData && serverData.id ? mapBackendMessage({ ...serverData, sender_id: serverData.sender_id ?? currentUser?.company_id }, currentUser?.company_id) : null;
                if (serverMsg) {
                    setConversations((prev) =>
                        prev.map((conv) =>
                            conv.id === activeConvId
                                ? { ...conv, messages: [...conv.messages, serverMsg] }
                                : conv
                        )
                    );
                } else {
                    setConversations((prev) =>
                        prev.map((conv) => (conv.id === activeConvId ? { ...conv, messages: [...conv.messages, newMsg] } : conv))
                    );
                }
                setMessageText('');
                setFilePreview(null);
                if (fileRef.current) fileRef.current.value = null;
                if (imageRef.current) imageRef.current.value = null;
                setSending(false);
                setShowEmojiPicker(false);
                return;
            }
            // No accepted flow yet -> fall through to a local draft.
        }

        await new Promise((r) => setTimeout(r, 300));

        setConversations((prev) =>
            prev.map((conv) => (conv.id === activeConvId ? { ...conv, messages: [...conv.messages, newMsg] } : conv))
        );

        setMessageText('');
        setFilePreview(null);
        if (fileRef.current) fileRef.current.value = null;
        if (imageRef.current) imageRef.current.value = null;
        setSending(false);
        setShowEmojiPicker(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const handleSendOffer = (ask) => {
        setSelectedAsk(ask);
        setShowOfferCreator(true);
        // Reset offer data when opening, pre-filling location with the
        // logged-in user's saved address when available.
        setOfferData({
            price: ask.price || '',
            location: currentUser?.company_address || ask.location || '',
            latitude: null,
            longitude: null,
            deliveryOption: 'pickup',
            personalNote: '',
            media: null,
            quantity: 1
        });
    };

    const handleOfferDataChange = (field, value) => {
        setOfferData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const uploadOfferMedia = async (media) => {
        if (!media?.file) {
            return { image_urls: null, video_urls: null, url: media?.url || '' };
        }
        const uploadData = new FormData();
        uploadData.append('file', media.file);
        const uploadResponse = await api.post(API_CONFIG.PRODUCTS.UPLOAD, uploadData, {
            headers: { 'Content-Type': 'multipart/form-data' },
            timeout: 300000
        });
        const uploadResult = uploadResponse.data;
        if (!uploadResult.success) {
            throw new Error(uploadResult.error || 'Failed to upload media');
        }
        const fileUrl = uploadResult.url || uploadResult.fileUrl || uploadResult.secure_url;
        if (!fileUrl) {
            throw new Error('Upload completed but no file URL returned');
        }
        const isVideo = media.type === 'video' || String(media.file.type || '').startsWith('video/');
        return isVideo
            ? { image_urls: null, video_urls: [fileUrl], url: fileUrl }
            : { image_urls: [fileUrl], video_urls: null, url: fileUrl };
    };

    const sendOffer = async (finalOfferData) => {
        if (!finalOfferData.price || finalOfferData.price <= 0) {
            showAlertMessage('Please enter a valid price');
            return;
        }
        
        if (!finalOfferData.location) {
            showAlertMessage('Please enter your location');
            return;
        }

        try {
            let mediaUrls = { image_urls: null, video_urls: null, url: '' };
            if (finalOfferData.media?.file) {
                showAlertMessage('Uploading media…');
                mediaUrls = await uploadOfferMedia(finalOfferData.media);
            } else if (finalOfferData.media?.url) {
                mediaUrls = { image_urls: null, video_urls: null, url: finalOfferData.media.url };
            }

            // Submit offer using the service
            const result = await submitOfferToAsk(selectedAsk.askId || selectedAsk.id, {
                price: finalOfferData.price,
                offer: finalOfferData.personalNote || 'Offer submitted',
                quantity: finalOfferData.quantity || 1,
                delivery_time: finalOfferData.deliveryOption,
                notes: finalOfferData.location,
                latitude: finalOfferData.latitude,
                longitude: finalOfferData.longitude,
                image_urls: mediaUrls.image_urls,
                video_urls: mediaUrls.video_urls
            });
            
            if (result.success) {
                const offerMessage = {
                    id: `m-${Date.now()}-offer`,
                    sender: 'seller',
                    senderName: 'You',
                    text: `💰 Offer Sent`,
                    time: new Date().toISOString(),
                    images: mediaUrls.url ? [mediaUrls.url] : (finalOfferData.media ? [finalOfferData.media.url] : []),
                    reactions: {},
                    isOffer: true,
                    offerData: {
                        price: finalOfferData.price,
                        location: finalOfferData.location,
                        deliveryOption: finalOfferData.deliveryOption,
                        personalNote: finalOfferData.personalNote,
                        quantity: finalOfferData.quantity,
                        title: selectedAsk.title,
                        timestamp: new Date().toISOString(),
                        offerId: result.data?.response_id || result.data?.id || null
                    }
                };

                setConversations((prev) =>
                    prev.map((conv) => 
                        conv.id === activeConvId 
                            ? { ...conv, messages: [...conv.messages, offerMessage] } 
                            : conv
                    )
                );

                setShowOfferCreator(false);
                showAlertMessage(`✅ Offer sent for: ${selectedAsk.title}`);
            } else {
                showAlertMessage(result.error || 'Failed to send offer', true);
            }
        } catch (err) {
            console.error('Failed to send offer:', err);
            showAlertMessage('Failed to send offer: ' + err.message, true);
        }
    };

    const handleRequestMoreInfo = () => {
        const infoMessage = {
            id: `m-${Date.now()}-info`,
            sender: 'seller',
            senderName: 'You',
            text: "Could you please provide more details about your requirements? This will help me prepare the best solution for you.",
            time: new Date().toISOString(),
            images: [],
            reactions: {}
        };
        
        setConversations((prev) =>
            prev.map((conv) => 
                conv.id === activeConvId 
                    ? { ...conv, messages: [...conv.messages, infoMessage] } 
                    : conv
            )
        );
        showAlertMessage('Request sent for more information');
    };

    const handleMarkAsWon = () => {
        setConversations((prev) =>
            prev.map((conv) => 
                conv.id === activeConvId 
                    ? { ...conv, status: 'won' } 
                    : conv
            )
        );
        showAlertMessage(`🎉 Marked "${activeConv.title}" as won!`);
    };

    const handleNoteChange = (e) => {
        setNotes(prev => ({
            ...prev,
            [activeConvId]: e.target.value
        }));
    };

    const addEmoji = (emoji) => {
        setMessageText(prev => prev + emoji);
        setShowEmojiPicker(false);
    };

    const commonEmojis = ['😊', '👍', '❤️', '😂', '😍', '🙏', '🎉', '🤔', '👏', '🔥'];

    const handleRefresh = () => {
        if (isAuth) {
            loadAsks();
        }
    };

    const handleRetry = () => {
        if (isAuth) {
            loadAsks();
        }
    };

    const renderFilePreview = () => {
        if (!filePreview) return null;
        
        if (filePreview.type === 'image') {
            return (
                <div className="mb-3 flex items-center gap-3 bg-white p-3 rounded-lg border">
                    <img src={filePreview.dataUrl} alt="preview" className="w-16 h-16 object-cover rounded-md border" />
                    <div className="flex-1">
                        <div className="font-medium text-sm truncate">{filePreview.name}</div>
                        <button 
                            className="text-xs text-red-600 mt-1 hover:text-red-800"
                            onClick={() => { setFilePreview(null); if (imageRef.current) imageRef.current.value = null; }}
                        >
                            Remove
                        </button>
                    </div>
                </div>
            );
        } else {
            return (
                <div className="mb-3 flex items-center gap-3 bg-white p-3 rounded-lg border">
                    <div className="w-12 h-12 bg-gray-200 rounded-md flex items-center justify-center">
                        <span className="text-2xl">📎</span>
                    </div>
                    <div className="flex-1">
                        <div className="font-medium text-sm truncate">{filePreview.name}</div>
                        <div className="text-xs text-gray-500">{filePreview.size}</div>
                        <button 
                            className="text-xs text-red-600 mt-1 hover:text-red-800"
                            onClick={() => { setFilePreview(null); if (fileRef.current) fileRef.current.value = null; }}
                        >
                            Remove
                        </button>
                    </div>
                </div>
            );
        }
    };

    // Authentication required screen
    if (!isAuth) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md">
                    <div className="text-6xl mb-4">🔒</div>
                    <h3 className="text-xl font-semibold mb-2">Authentication Required</h3>
                    <p className="text-gray-600 mb-6">Please log in to view and respond to asks.</p>
                    <button 
                        onClick={handleRetry}
                        className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-medium"
                    >
                        Login
                    </button>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading asks...</p>
                </div>
            </div>
        );
    }

    if (error && conversations.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md">
                    <div className="text-red-600 text-6xl mb-4">⚠️</div>
                    <h3 className="text-xl font-semibold mb-2">Error Loading Asks</h3>
                    <p className="text-gray-600 mb-6">{error}</p>
                    <button 
                        onClick={handleRefresh}
                        className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-medium"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (conversations.length === 0) {
        return (
            <div className="flex items-center justify-center min-h-screen bg-gray-50">
                <div className="text-center bg-white p-8 rounded-2xl shadow-lg max-w-md">
                    <div className="text-6xl mb-4">📭</div>
                    <h3 className="text-xl font-semibold mb-2">No Asks Available</h3>
                    <p className="text-gray-600 mb-6">There are no asks to display at this time.</p>
                    <button 
                        onClick={handleRefresh}
                        className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition font-medium"
                    >
                        Refresh
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="lg:min-h-[90vh] min-h-[85vh] max-w-[100vw] overflow-auto flex flex-col bg-gray-100">
            {/* Alert Popup */}
            {showAlert && (
                <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-fade-in">
                    <div className={`px-6 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
                        alertMessage.includes('Failed') || alertMessage.includes('Error') 
                            ? 'bg-red-500' 
                            : 'bg-green-500'
                    } text-white`}>
                        <span className="font-medium">{alertMessage}</span>
                    </div>
                </div>
            )}

            {/* Offer Creator Popup */}
            {showOfferCreator && (
                <OfferCreator 
                    selectedAsk={selectedAsk}
                    offerData={offerData}
                    onOfferDataChange={handleOfferDataChange}
                    onSend={sendOffer}
                    onCancel={() => setShowOfferCreator(false)}
                />
            )}

            <div className="max-w-full my-auto p-2 md:p-4 lg:min-h-[90vh] h-[85vh]">
                <div className="bg-white rounded-lg min-h-[100%] shadow flex flex-col md:grid md:grid-cols-12 overflow-hidden">
                    {/* Mobile header */}
                    <div className="md:hidden flex justify-between items-center p-3 border-b bg-white">
                        <button 
                            onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
                            className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 transition"
                        >
                            {isMobileSidebarOpen ? '✕' : '☰'} Conversations
                        </button>
                        <div className="font-semibold truncate px-2 text-sm">
                            {activeConv?.title || 'Messages'}
                        </div>
                        <button 
                            onClick={() => setIsMobileDetailsOpen(!isMobileDetailsOpen)}
                            className="p-2 rounded-md bg-gray-100 hover:bg-gray-200 transition"
                        >
                            {isMobileDetailsOpen ? '✕' : '⋯'} Details
                        </button>
                    </div>

                    {/* Sidebar */}
                    <aside className={`${isMobileSidebarOpen ? 'block' : 'hidden'} md:block col-span-12 md:col-span-3 border-r border-gray-200 p-3 bg-white z-10 absolute md:relative h-full md:h-auto w-full md:w-auto overflow-auto`}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="font-semibold text-lg">Shop Asks</h3>
                            <button
                                className="text-sm px-3 py-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition shadow-sm"
                                onClick={handleRefresh}
                            >
                                🔄 Refresh
                            </button>
                        </div>

                        <div className="space-y-2" style={{ maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' }}>
                            {conversations.map((conv) => (
                                <button
                                    key={conv.id}
                                    onClick={() => handleSelectConversation(conv.id)}
                                    className={`w-full text-left p-3 rounded-lg flex items-start gap-3 hover:bg-gray-50 transition ${
                                        conv.id === activeConvId ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                                    }`}
                                >
                                    {conv.avatarUrl ? (
                                        <img
                                            src={conv.avatarUrl}
                                            alt={conv.buyerName}
                                            className="w-10 h-10 rounded-full object-cover"
                                        />
                                    ) : (
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white ${conv.avatarColor}`}>
                                            {(conv.buyerName || conv.title).split(' ').slice(0,1)[0]?.slice(0,1).toUpperCase()}
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center justify-between">
                                            <div className="font-medium text-sm truncate">{conv.buyerName || conv.title}</div>
                                            <div className="text-xs text-gray-400">{conv.messages.length} msg</div>
                                        </div>
                                        <p className="text-xs text-gray-500 truncate mt-1">{conv.snippet}</p>
                                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                                            {conv.unread > 0 && (
                                                <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{conv.unread} new</span>
                                            )}
                                            {conv.status === 'won' && (
                                                <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Won</span>
                                            )}
                                            {conv.price && (
                                                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                                    ₦{parseFloat(conv.price).toLocaleString()}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </aside>

                    {/* Main chat area */}
                    <main className="col-span-12 md:col-span-6 p-3 md:p-4 flex flex-col bg-white">
                        {/* Header */}
                        <div className="border-b border-gray-200 pb-3 mb-3 flex items-center justify-between">
                            <div className="flex items-center gap-3 min-w-0">
                                {activeConv?.avatarUrl ? (
                                    <img
                                        src={activeConv.avatarUrl}
                                        alt={activeConv?.buyerName}
                                        className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover"
                                    />
                                ) : (
                                    <div className={`w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white ${activeConv?.avatarColor || 'bg-indigo-500'}`}>
                                        {(activeConv?.buyerName || activeConv?.title || '?').slice(0,1).toUpperCase()}
                                    </div>
                                )}
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold truncate text-sm md:text-base">{activeConv?.title}</div>
                                    <div className="text-xs text-gray-500 truncate">From: {activeConv?.buyerName}</div>
                                </div>
                            </div>
                            <div className="text-xs text-gray-500 hidden sm:block">{activeConv?.messages?.length || 0} messages</div>
                        </div>

                        {/* Messages list */}
                        <div className="flex-1 overflow-auto px-1 md:px-2" style={{ paddingBottom: 12, maxHeight: 'calc(100vh - 300px)' }}>
                            <div className="space-y-4">
                                {activeConv?.messages?.map((m) => (
                                    <div key={m.id} className={`flex ${m.sender === 'seller' ? 'justify-end' : 'justify-start'}`}>
                                        <div className={`inline-block rounded-3xl p-3 shadow-md text-[15px] leading-snug break-words ${
                                            m.sender === 'seller'
                                                ? 'bg-blue-600 text-white rounded-br-sm'
                                                : 'bg-white dark:bg-gray-800 text-black dark:text-white border border-gray-200 rounded-bl-sm'
                                        }`} style={{ maxWidth: '85%' }}>
                                            {m.isOffer && (
                                                <div className="mb-3 p-3 rounded-2xl shadow-sm">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className="text-xl">💰</span>
                                                        <span className="font-bold">{m.sender === 'seller' ? 'Offer Sent' : 'Offer Received'}</span>
                                                    </div>
                                                    <div className="text-sm space-y-1">
                                                        <div className="flex justify-between gap-4">
                                                            <span>Price:</span>
                                                            <span className="font-bold">₦{Number(m.offerData?.price || 0).toLocaleString()}</span>
                                                        </div>
                                                        <div className="flex justify-between gap-4">
                                                            <span>Location:</span>
                                                            <span>{m.offerData?.location || '—'}</span>
                                                        </div>
                                                        <div className="flex justify-between gap-4">
                                                            <span>Delivery:</span>
                                                            <span className="capitalize">{m.offerData?.deliveryOption || 'pickup'}</span>
                                                        </div>
                                                        {m.offerData?.quantity > 1 && (
                                                            <div className="flex justify-between gap-4">
                                                                <span>Quantity:</span>
                                                                <span>{m.offerData.quantity} units</span>
                                                            </div>
                                                        )}
                                                        {m.offerData?.personalNote && (
                                                            <div className="mt-2 pt-2 border-t border-opacity-30">
                                                                <span>💬 Note: </span>
                                                                {m.offerData.personalNote}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            {m.text && <div className="whitespace-pre-wrap">{m.text}</div>}
                                            
                                            {m.images?.length > 0 && (
                                                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                                                    {m.images.map((img, i) => (
                                                        <img key={i} src={img} alt={`attachment-${i}`} className="w-full h-20 md:h-24 object-cover rounded-lg border" />
                                                    ))}
                                                </div>
                                            )}
                                            
                                            {m.file && (
                                                <div className="mt-2 flex items-center gap-3 bg-white/20 rounded-lg p-2">
                                                    <div className="w-10 h-10 bg-gray-200/20 rounded-md flex items-center justify-center">
                                                        <span className="text-xl">📎</span>
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="font-medium text-sm truncate">{m.file.name}</div>
                                                        <div className="text-xs opacity-70">{m.file.size}</div>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            <div className={`mt-1.5 flex items-center gap-1 text-[11px] ${m.sender === 'seller' ? 'justify-end text-white/70' : 'text-gray-400'}`}>
                                                <span>{new Date(m.time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                <div ref={messagesEndRef} />
                            </div>
                        </div>

                        {/* Conditional Composer */}
                        <div className="mt-4 border-t border-gray-200 pt-3 bg-white">
                            {!hasSentOffer ? (
                                <div className="text-center py-8">
                                    <div className="mb-4">
                                        <div className="text-5xl mb-2">💼</div>
                                        <p className="text-gray-600 text-sm">Send a formal offer to the buyer with your pricing and terms</p>
                                    </div>
                                    <button
                                        onClick={() => handleSendOffer(activeConv)}
                                        className="bg-blue-600 text-white px-8 py-3 rounded-xl hover:bg-blue-700 transition font-medium shadow-lg"
                                    >
                                        + Send Offer
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {/* Offer Status Banner */}
                                    <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                                        <div className="flex items-center gap-3">
                                            <div className="text-yellow-600 text-2xl">⏳</div>
                                            <div>
                                                <div className="font-medium text-yellow-800">Offer Sent - Waiting for Response</div>
                                                <div className="text-yellow-600 text-sm">Your offer has been submitted. The buyer will review it shortly.</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Messaging Interface */}
                                    {renderFilePreview()}

                                    {/* File Options Popup */}
                                    {showFileOptions && (
                                        <div className="mb-3 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                                            <div className="flex gap-3">
                                                <button 
                                                    onClick={() => imageRef.current?.click()}
                                                    className="flex flex-col items-center gap-2 p-3 hover:bg-gray-50 rounded-lg transition flex-1"
                                                >
                                                    <span className="text-2xl">🖼️</span>
                                                    <span className="text-xs">Photo</span>
                                                </button>
                                                <button 
                                                    onClick={() => fileRef.current?.click()}
                                                    className="flex flex-col items-center gap-2 p-3 hover:bg-gray-50 rounded-lg transition flex-1"
                                                >
                                                    <span className="text-2xl">📎</span>
                                                    <span className="text-xs">File</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Emoji Picker */}
                                    {showEmojiPicker && (
                                        <div className="mb-3 bg-white border border-gray-200 rounded-lg shadow-lg p-3">
                                            <div className="grid grid-cols-10 gap-1">
                                                {commonEmojis.map((emoji, index) => (
                                                    <button
                                                        key={index}
                                                        onClick={() => addEmoji(emoji)}
                                                        className="p-1 hover:bg-gray-100 rounded transition text-lg"
                                                    >
                                                        {emoji}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="flex gap-2 items-end">
                                        <div className="relative">
                                            <button 
                                                onClick={() => setShowFileOptions(!showFileOptions)}
                                                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition"
                                            >
                                                <span className="text-xl">➕</span>
                                            </button>
                                            
                                            <input 
                                                ref={imageRef} 
                                                type="file" 
                                                accept="image/*" 
                                                onChange={handleFileChange} 
                                                className="hidden" 
                                            />
                                            <input 
                                                ref={fileRef} 
                                                type="file" 
                                                onChange={handleFileChange} 
                                                className="hidden" 
                                            />
                                        </div>

                                        <div className="flex-1 bg-gray-100 rounded-3xl px-4 py-2 flex items-center">
                                            <button 
                                                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                                                className="p-1 text-gray-500 hover:text-gray-700 mr-2 transition"
                                            >
                                                <span className="text-xl">😊</span>
                                            </button>
                                            
                                            <textarea
                                                ref={textareaRef}
                                                rows={1}
                                                value={messageText}
                                                onChange={(e) => setMessageText(e.target.value)}
                                                onKeyDown={handleKeyDown}
                                                placeholder="Type a message..."
                                                className="w-full resize-none bg-transparent border-none outline-none py-1 text-sm max-h-32"
                                            />
                                        </div>

                                        <button 
                                            onClick={sendMessage} 
                                            disabled={sending || (!messageText.trim() && !filePreview)}
                                            className={`p-3 rounded-full transition ${
                                                sending || (!messageText.trim() && !filePreview)
                                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                    : 'bg-blue-500 text-white hover:bg-blue-600'
                                            }`}
                                        >
                                            {sending ? (
                                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                            ) : (
                                                <span className="text-lg">➤</span>
                                            )}
                                        </button>
                                    </div>
                                </>
                            )}
                        </div>
                    </main>

                    {/* Right details panel */}
                    <aside className={`${isMobileDetailsOpen ? 'block' : 'hidden'} md:block col-span-12 md:col-span-3 border-l border-gray-200 p-3 md:p-4 bg-white z-10 absolute md:relative h-full md:h-auto right-0 top-0 w-full md:w-auto overflow-auto`}>
                        <div className="mb-4">
                            <h4 className="font-semibold text-lg mb-3">Ask Details</h4>
                            <div className="space-y-2">
                                <div>
                                    <label className="text-xs text-gray-500 block">Request</label>
                                    <p className="text-sm font-medium truncate">{activeConv?.title}</p>
                                </div>
                                {activeConv?.price && (
                                    <div>
                                        <label className="text-xs text-gray-500 block">Budget</label>
                                        <p className="text-sm font-medium text-green-600">₦{parseFloat(activeConv.price).toLocaleString()}</p>
                                    </div>
                                )}
                                {activeConv?.location && (
                                    <div>
                                        <label className="text-xs text-gray-500 block">Location</label>
                                        <p className="text-sm">{activeConv.location}</p>
                                    </div>
                                )}
                                {activeConv?.category && (
                                    <div>
                                        <label className="text-xs text-gray-500 block">Category</label>
                                        <p className="text-sm">{activeConv.category}</p>
                                    </div>
                                )}
                                <div>
                                    <label className="text-xs text-gray-500 block">Participants</label>
                                    <div className="flex items-center gap-2 mt-1">
                                        {activeConv?.avatarUrl ? (
                                            <img
                                                src={activeConv.avatarUrl}
                                                alt={activeConv?.buyerName}
                                                className="w-6 h-6 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className={`w-6 h-6 rounded-full flex items-center justify-center text-white text-xs ${activeConv?.avatarColor || 'bg-blue-400'}`}>
                                                {(activeConv?.buyerName || activeConv?.title || '?').slice(0,1).toUpperCase()}
                                            </div>
                                        )}
                                        <p className="text-sm">
                                            You (Seller) & <span className="font-medium">{activeConv?.buyerName}</span> (Buyer)
                                        </p>
                                    </div>
                                </div>
                                {activeConv?.status === 'won' && (
                                    <div className="mt-2">
                                        <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full inline-block">🎉 Deal Won</span>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="mb-4">
                            <h5 className="font-medium text-sm mb-2">Quick actions</h5>
                            <div className="flex flex-col gap-2">
                                {!hasSentOffer ? (
                                    <button 
                                        onClick={() => handleSendOffer(activeConv)}
                                        className="text-left px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm shadow-sm"
                                    >
                                        💰 Send Offer
                                    </button>
                                ) : (
                                    <button 
                                        onClick={() => handleSendOffer(activeConv)}
                                        className="text-left px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition text-sm shadow-sm"
                                    >
                                        💰 Send New Offer
                                    </button>
                                )}
                                {/* <button 
                                    onClick={handleRequestMoreInfo}
                                    className="text-left px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition text-sm shadow-sm"
                                >
                                    ℹ️ Request More Info
                                </button>
                         */}
                            </div>
                        </div>

                        {/* <div>
                            <h5 className="font-medium text-sm mb-2">Internal Notes</h5>
                            <textarea 
                                value={notes[activeConvId] || ''} 
                                onChange={handleNoteChange}
                                className="w-full mt-2 p-3 border border-gray-200 rounded-lg h-36 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 resize-none" 
                                placeholder="Add internal notes about this lead..."
                            />
                        </div> */}
                    </aside>
                </div>
            </div>

            <style jsx>{`
                @keyframes fade-in {
                    from { opacity: 0; transform: translate(-50%, -10px); }
                    to { opacity: 1; transform: translate(-50%, 0); }
                }
                @keyframes scale-in {
                    from { opacity: 0; transform: scale(0.9); }
                    to { opacity: 1; transform: scale(1); }
                }
                .animate-fade-in {
                    animation: fade-in 0.3s ease-out;
                }
                .animate-scale-in {
                    animation: scale-in 0.2s ease-out;
                }
            `}</style>
        </div>
    );
}